import { useMemo, useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEmployeesMock } from '@/entities/employee/api/hooks';
import { useAddContingentRecord, useContingentHistory, useContingentRecords } from '@/entities/medical/api/hooks';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import sharedExpanded from '@/shared/ui/ExpandedRows/ExpandedRows.module.scss';
import { SearchInput } from '@/shared/ui/SearchInput';
import type { ContingentRecord } from '@/entities/medical/model/medicalSchema';
import type { Employee } from '@/entities/employee/model/employeeSchema';
import s from './ContingentTab.module.scss';
import { exportContingentCsv } from '../utils';

const { Text } = Typography;

export function ContingentTab() {
  const { data, isLoading } = useContingentRecords();
  const { data: historyData, isLoading: historyLoading } = useContingentHistory();
  const { data: allEmployees } = useEmployeesMock();
  const addContingent = useAddContingentRecord();
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();

  const departmentOptions = useMemo(() => {
    const set = new Set((data ?? []).map((r) => r.department).filter(Boolean));
    return [{ value: '', label: 'Все подразделения' }, ...[...set].sort().map((d) => ({ value: d, label: d }))];
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((r) => {
      const matchesSearch =
        !q ||
        r.position.toLowerCase().includes(q) ||
        r.department.toLowerCase().includes(q) ||
        r.harmfulFactors.toLowerCase().includes(q);
      const matchesDept =
        !department ||
        r.department === department ||
        r.department
          .split(',')
          .map((x) => x.trim())
          .includes(department);
      return matchesSearch && matchesDept;
    });
  }, [data, search, department]);

  const matchedEmployees = useMemo(() => {
    if (!allEmployees || !data) return new Map<number, Employee[]>();
    const map = new Map<number, Employee[]>();
    for (const cont of data) {
      const posLower = cont.position.toLowerCase();
      const depts = cont.department.split(',').map((d) => d.trim());
      const matched = allEmployees.filter((e) => {
        const posMatch = e.position.toLowerCase().includes(posLower);
        const deptMatch = depts.some((d) => e.department.includes(d));
        return posMatch && deptMatch;
      });
      map.set(cont.id, matched);
    }
    return map;
  }, [allEmployees, data]);

  const contColumns: ColumnsType<ContingentRecord> = [
    { title: 'Профессия', dataIndex: 'position', key: 'pos' },
    { title: 'Подразделение', dataIndex: 'department', key: 'dept' },
    { title: 'Вредные факторы', dataIndex: 'harmfulFactors', key: 'factors' },
    { title: 'Вид осмотра', dataIndex: 'examType', key: 'type' },
    { title: 'Основание включения', dataIndex: 'legalBasis', key: 'basis' },
    { title: 'Кол-во', dataIndex: 'employeeCount', key: 'count', width: 80 },
  ];

  const historyEntries = [...(historyData ?? [])].reverse();

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <SearchInput
          placeholder="Поиск..."
          className={s['root__filter-input']}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <Select
          className={s['root__filter-select-narrow']}
          value={department}
          onChange={(v) => setDepartment(v)}
          options={departmentOptions}
        />
        <Button type="primary" onClick={() => setAddOpen(true)}>
          Добавить позицию
        </Button>
        <Button onClick={() => message.success('Контингент обновлён из Р7')}>Обновить контингент</Button>
        <Button onClick={() => exportContingentCsv(filtered, 'Контингент_Р7')}>Выгрузить список</Button>
      </Space>

      <Table
        loading={isLoading}
        dataSource={filtered}
        columns={contColumns}
        rowKey="id"
        size="small"
        pagination={{ ...antTablePagination }}
        scroll={antTableScroll}
        expandable={{
          expandedRowRender: (record) => {
            const emps = matchedEmployees.get(record.id) ?? [];
            if (!emps.length) {
              return (
                <div className={sharedExpanded.expanded}>
                  <Text type="secondary">Нет совпадений среди загруженных сотрудников</Text>
                </div>
              );
            }
            return (
              <div className={sharedExpanded.expanded}>
                <Text type="secondary" className={sharedExpanded.hint}>
                  Сотрудники, включённые в контингент:
                </Text>
                {emps.map((e) => (
                  <div key={e.id} className={sharedExpanded.row}>
                    <span className={s['root__expanded-card-name']}>{e.name}</span>
                    <span className={sharedExpanded.meta}>
                      {e.position}, {e.department}
                    </span>
                    <Tag>{record.legalBasis}</Tag>
                  </div>
                ))}
              </div>
            );
          },
          rowExpandable: () => true,
        }}
      />

      <Collapse
        className={s['root__contingent-history-collapse']}
        items={[
          {
            key: 'history',
            label: 'История изменений контингента',
            children: historyLoading ? (
              <p>Загрузка…</p>
            ) : historyEntries.length === 0 ? (
              <p className={s['root__muted-hint']}>Нет записей. Добавьте позицию, чтобы увидеть историю.</p>
            ) : (
              <ul className={s['root__history-list']}>
                {historyEntries.map((h, i) => (
                  <li key={`${h.date}-${i}`}>
                    {h.date} — {h.action}
                  </li>
                ))}
              </ul>
            ),
          },
        ]}
      />

      <Modal
        title="Новая позиция контингента"
        open={addOpen}
        confirmLoading={addContingent.isPending}
        onCancel={() => {
          setAddOpen(false);
          addForm.resetFields();
        }}
        onOk={() => {
          void addForm.validateFields().then((values) => {
            addContingent.mutate(
              {
                position: values.position as string,
                department: values.department as string,
                harmfulFactors: values.harmfulFactors as string,
                examType: values.examType as string,
                legalBasis: values.legalBasis as string,
                employeeCount: values.employeeCount as number,
              },
              {
                onSuccess: () => {
                  message.success('Позиция добавлена');
                  setAddOpen(false);
                  addForm.resetFields();
                },
              },
            );
          });
        }}
        okText="Сохранить"
        destroyOnClose
        width={560}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="position" label="Профессия" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="department" label="Подразделение" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="harmfulFactors" label="Вредные факторы" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="examType" label="Вид осмотра" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="legalBasis" label="Основание включения" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="employeeCount" label="Количество сотрудников" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
